import { Component, DestroyRef, effect, inject, input, NO_ERRORS_SCHEMA, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
	ButtonIconComponent,
	ITableConfig,
	ITableHeader,
	ITableRow,
	TableComponent,
	TileComponent,
} from '@tecalisdev/infinity';
import { Subscription } from 'rxjs/internal/Subscription';
import { finalize } from 'rxjs/internal/operators/finalize';
import { RolePermissionsService } from '../../../core/services/role-permissions.service';
import { formatFilterTable } from '../../../core/utils/utils';
import { ModalCurlRequestComponent } from '../../../shared/components/modal-curl-request/modal-curl-request.component';
import { LoadingService } from '../../../shared/services/loading.service';
import { ITopNationalityProcessesBody } from '../../models/panel.models';
import { PanelCurlService } from '../../services/panel-curl.service';
import { PanelHelperService } from '../../services/panel-helper.service';
import { PanelService } from '../../services/panel.service';

@Component({
	selector: 'app-top-nationalities',
	imports: [TranslatePipe, TableComponent, TileComponent, ButtonIconComponent, ModalCurlRequestComponent],
	templateUrl: './top-nationalities.component.html',
	styleUrl: './top-nationalities.component.scss',
	schemas: [NO_ERRORS_SCHEMA],
})
export class TopNationalitiesComponent {
	private static readonly LIMIT = 5;

	private panelService = inject(PanelService);
	private panelHelperService = inject(PanelHelperService);
	protected rolePermissions = inject(RolePermissionsService);
	private panelCurlService = inject(PanelCurlService);
	private loading = inject(LoadingService);
	private destroyRef = inject(DestroyRef);
	private request?: Subscription;

	/** Sin tile ni título cuando vive dentro de tabs del panel (NIF-591). */
	public readonly embedded = input(false);

	public header: ITableHeader[] = [{ label: 'Nacionalidad' }, { label: 'Cantidad' }];
	public content: ITableRow[] = [];
	public config: ITableConfig = { clickeable: false, checkbox: false, borderless: true };
	public readonly curlModalOpen = signal(false);

	constructor() {
		this.destroyRef.onDestroy(() => this.request?.unsubscribe());
		effect(() => {
			const rawFilters = this.panelHelperService.currentFilters();
			if (rawFilters) {
				const filters = { ...formatFilterTable(rawFilters) };
				this.loadNationalities(filters);
			}
		});
	}

	public openCurlModal(): void {
		this.curlModalOpen.set(true);
	}

	public closeCurlModal(): void {
		this.curlModalOpen.set(false);
	}

	public getTopNationalityCurl(): string {
		const rawFilters = this.panelHelperService.currentFilters();
		if (!rawFilters) return '';
		const filters = { ...formatFilterTable({ ...rawFilters }) };
		return this.panelCurlService.buildTopNationalityProcessesCurl(filters);
	}

	private loadNationalities(filters: any): void {
		this.request?.unsubscribe();
		this.loading.showTable(this.config, this.header.length, TopNationalitiesComponent.LIMIT);
		this.request = this.panelService
			.getTopNationalityProcesses(filters)
			.pipe(finalize(() => this.loading.hideTable(this.config)))
			.subscribe({
				next: (response) => {
					const data = response?.data;
					this.content = Array.isArray(data) ? this.transformDataToTableRows(data) : [];
				},
				error: () => {
					this.content = [];
				},
			});
	}

	private transformDataToTableRows(data: ITopNationalityProcessesBody[]): ITableRow[] {
		return data.map((item, index) => ({
			clickValue: null,
			cells: [
				{
					// Sin 'N/A': el API ya excluye null/vacío/**** (NIF-591).
					label: item.nationality ?? '',
					badge: { label: (index + 1).toString(), status: 'on', type: 'default' },
				},
				{
					label: '',
					badge: { label: item.processes?.toString() || '0', type: 'square' },
					align: 'right',
					classes: ['top-nationalities__count-cell'],
				},
			],
		}));
	}
}
